import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { X, Info, AlertTriangle, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Billing status banner shown at the top of the app shell.
 *
 * Visibility rules:
 *  - TP-Billing-RBAC (Feb 2026) -- only Workspace Admin (company_admin)
 *    sees billing banners. Team Admin, Employee and Super Admin all
 *    skip the surface so commercial messaging never leaks to non-
 *    owner roles. Plan gating is unchanged; this is visibility only.
 *  - Info banners are dismissible (sessionStorage), warning/danger sticky.
 */
const TONE_STYLES = {
  info:    { wrap: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
             icon: Info, iconCls: "text-[#1D4ED8]" },
  warning: { wrap: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
             icon: AlertTriangle, iconCls: "text-[#B45309]" },
  danger:  { wrap: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
             icon: AlertCircle, iconCls: "text-[#B91C1C]" },
};

export default function BillingBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [dismissedKey, setDismissedKey] = useState(() => {
    // Lazy initializer reads sessionStorage once on mount - cleaner than
    // a useEffect that immediately sets state.
    try { return sessionStorage.getItem("nextrota.billing-banner.dismissed") || null; }
    catch { return null; }
  });

  // TP-Billing-RBAC (Feb 2026) -- only Workspace Admin sees billing
  // banners. Skip the /billing/me call entirely for every other role.
  const role = user?.role;
  const eligible = !!user && role === "company_admin";

  useEffect(() => {
    if (!eligible) return;
    let alive = true;
    api.get("/billing/me")
      .then(r => { if (alive) setState(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [eligible]);

  if (!eligible) return null;
  const banner = state?.access?.banner;
  if (!banner) return null;
  const tone = banner.tone || "info";
  const cfg = TONE_STYLES[tone] || TONE_STYLES.info;
  const Icon = cfg.icon;

  // Build a stable key from title+message so the dismiss state only persists
  // for the exact banner content we are currently showing.
  const bannerKey = `${tone}|${banner.title}|${banner.message}`;
  const isDismissed = tone === "info" && dismissedKey === bannerKey;
  if (isDismissed) return null;

  const dismiss = () => {
    setDismissedKey(bannerKey);
    try { sessionStorage.setItem("nextrota.billing-banner.dismissed", bannerKey); } catch { /* ignore */ }
  };

  const isAdmin = role === "company_admin";

  return (
    <div data-testid={`billing-banner-${tone}`}
         className={`border-b ${cfg.wrap} px-4 sm:px-6 py-2.5 text-sm flex items-start sm:items-center justify-between gap-3`}>
      <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
        <Icon className={`h-4 w-4 shrink-0 mt-0.5 sm:mt-0 ${cfg.iconCls}`} />
        <div className="min-w-0">
          <span className="font-semibold">{banner.title}</span>
          <span className="hidden sm:inline mx-2 opacity-50">·</span>
          <span className="block sm:inline opacity-90">{banner.message}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isAdmin && (
          <button
            type="button"
            data-testid="billing-banner-cta"
            onClick={() => navigate("/app/billing")}
            className="text-xs font-semibold rounded-md px-3 py-1.5 bg-white/60 hover:bg-white/90 border border-current/20 whitespace-nowrap">
            Open Billing →
          </button>
        )}
        {tone === "info" && (
          <button
            type="button"
            data-testid="billing-banner-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="h-7 w-7 grid place-items-center rounded-md hover:bg-white/40">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
